import type {
  Pathway,
  PathwayDetail,
  PathwayOutcome,
  PathwayProgress,
} from '../../schema/be-graphql-generated';
import {
  ASSIGNABLE_KINDS,
  targetTypeOf,
  videoTargetOf,
  type AssignableKind,
} from '../homework/model';

/**
 * E34 · Pathways on the portal (block 3a) — the pieces every pathway screen shares: the fields each read
 * asks for, the Builder's draft and its wire shape, and small PURE helpers (no i18n here: helpers return
 * KEYS and numbers, the screens translate).
 *
 * THE STEP KINDS THE BUILDER OFFERS are the Assign drawer's (`ASSIGNABLE_KINDS` — "the Target picker is the
 * assignment drawer's"): tool · assessment · worksheet · reading · task · link · video (block 6's player opened it — F-172). The
 * model's `session` step arrives with the block that lets the phone open it (E46): the Builder does not offer
 * what the phone cannot yet open (question Q-b3-5). A pathway that already carries one (a CopingCard program,
 * a copy) keeps it — the Builder shows it read-only.
 */

export const PATHWAY_ROW_FIELDS = [
  'pathwayId',
  'practiceId',
  'name',
  'description',
  'visibility',
  'version',
  'stepsCount',
  'status',
  'enrolledCount',
  'runningCount',
  'completedCount',
  'copiedFrom',
  'copiedFromName',
  'accessRule',
  'reviewStatus',
  'reason',
  'occurredAt',
];

export const PATHWAY_LIBRARY_FIELDS = [
  'capabilities',
  { pathways: PATHWAY_ROW_FIELDS },
];

const RELEASE_FIELDS = { release: ['rule', 'day', 'delayDays'] };
const TARGET_FIELDS = { target: ['type', 'ref', 'version', 'source'] };

export const PATHWAY_DETAIL_FIELDS = [
  'pathwayId',
  'practiceId',
  'name',
  'description',
  'visibility',
  'version',
  {
    sections: [
      'section',
      {
        steps: ['stepId', 'kind', TARGET_FIELDS, 'title', 'instructions', RELEASE_FIELDS, 'dueDays'],
      },
    ],
  },
  'copiedFrom',
  'copiedFromName',
  'retired',
  'library',
  'runningEnrollments',
  'accessRule',
  'reviewStatus',
  'reason',
];

export const PATHWAY_PROGRESS_FIELDS = [
  'enrollmentId',
  'subject',
  'requester',
  'requesterName',
  'practiceId',
  'pathwayId',
  'pathwayVersion',
  'pathwayName',
  'startAt',
  'status',
  'endedAt',
  'reason',
  {
    sections: [
      'section',
      {
        steps: [
          'stepId',
          'kind',
          TARGET_FIELDS,
          'title',
          'instructions',
          RELEASE_FIELDS,
          'dueDays',
          'state',
          'releasedAt',
          'assignmentId',
          'outcomeAt',
          { evidence: ['focusType', 'focus'] },
          'cancelled',
          'releaseAt',
        ],
      },
    ],
  },
  'percentDone',
  'stepsTotal',
  'stepsDone',
  'stepsSkipped',
  'selfEnrolled',
];

export const PATHWAY_OUTCOME_FIELDS = [
  'pathwayId',
  'pathwayName',
  'copiedFrom',
  'copiedFromName',
  'depth',
  'retired',
  'enrolledCount',
  'runningCount',
  'completedCount',
  'withdrawnCount',
  'completionRate',
  'medianDaysToComplete',
];

export type PathwayRow = Pathway;
export type PathwayLibrary = { capabilities: string[]; pathways: PathwayRow[] };
export type PathwayProgressRow = PathwayProgress;
export type PathwayOutcomeRow = PathwayOutcome;

export const PATHWAY_VISIBILITIES = ['practice', 'public'] as const;
export const RELEASE_RULES = ['immediately', 'onDay', 'afterPrevious', 'manual'] as const;
export type ReleaseRuleName = (typeof RELEASE_RULES)[number];

/** The step kinds the Builder offers — the Assign drawer's (see the note above). */
export const STEP_KINDS = ASSIGNABLE_KINDS;

// ── the Builder's draft ───────────────────────────────────────────────────────────────────────────

export type StepDraft = {
  stepId: string;
  kind: string;
  /** tool: the tool; assessment: the instrument; reading: the article; worksheet: the template; link: the URL; video: a Learn clip's id or the YouTube / Vimeo link. */
  targetRef: string | null;
  /** A target the Builder cannot edit (a `session` step it keeps as it is). */
  keptTarget: { type: string; ref?: string | null; version?: number | null; source?: string | null } | null;
  title: string;
  instructions: string;
  rule: ReleaseRuleName;
  day: number | null;
  delayDays: number | null;
  dueDays: number | null;
};

export type SectionDraft = { key: string; section: string; steps: StepDraft[] };

export type PathwayDraft = {
  name: string;
  description: string;
  visibility: 'practice' | 'public' | 'prescribeOnly';
  sections: SectionDraft[];
  copiedFrom: string | null;
  copiedFromName: string | null;
};

/** A step id unique in the pathway — `s1`, `s2`, … the first free one (ids are what releases key on). */
export const nextStepId = (sections: readonly SectionDraft[]): string => {
  const taken = new Set(sections.flatMap((s) => s.steps.map((st) => st.stepId)));
  for (let i = 1; ; i++) if (!taken.has(`s${i}`)) return `s${i}`;
};

let sectionSeq = 0;
export const sectionKey = (): string => `section-${Date.now().toString(36)}-${++sectionSeq}`;

export const freshStep = (sections: readonly SectionDraft[], first: boolean): StepDraft => ({
  stepId: nextStepId(sections),
  kind: 'reading',
  targetRef: null,
  keptTarget: null,
  title: '',
  instructions: '',
  // the first step cannot wait for a previous one (PATHWAY_FIRST_STEP_RULE)
  rule: first ? 'immediately' : 'afterPrevious',
  day: null,
  delayDays: first ? null : 0,
  dueDays: 3,
});

export const emptyPathwayDraft = (): PathwayDraft => ({
  name: '',
  description: '',
  visibility: 'practice',
  sections: [{ key: sectionKey(), section: '', steps: [] }],
  copiedFrom: null,
  copiedFromName: null,
});

const refOfTarget = (kind: string, target: PathwayDetail['sections'][number]['steps'][number]['target']) => {
  if (!target) return null;
  if (kind === 'tool') return target.type;
  return target.ref ?? null;
};

/** The Builder's draft of a loaded pathway — to edit it, or (a Duplicate) to save a copy of it. */
export const draftFromDetail = (
  d: PathwayDetail,
  mode: 'edit' | 'copy',
  copySuffix: string,
): PathwayDraft => ({
  name: mode === 'copy' ? `${d.name}${copySuffix}` : d.name,
  description: d.description ?? '',
  visibility: mode === 'copy' ? 'practice' : (d.visibility as PathwayDraft['visibility']),
  sections: d.sections.map((s) => ({
    key: sectionKey(),
    section: s.section,
    steps: s.steps.map((st) => {
      const editable = (STEP_KINDS as readonly string[]).includes(st.kind);
      return {
        stepId: st.stepId,
        kind: st.kind,
        targetRef: editable ? refOfTarget(st.kind, st.target) : null,
        keptTarget: editable ? null : st.target ?? null,
        title: st.title,
        instructions: st.instructions ?? '',
        rule: st.release.rule as ReleaseRuleName,
        day: st.release.day ?? null,
        delayDays: st.release.delayDays ?? null,
        dueDays: st.dueDays ?? null,
      };
    }),
  })),
  copiedFrom: mode === 'copy' ? d.pathwayId : null,
  copiedFromName: mode === 'copy' ? d.name : null,
});

/** A step's target on the wire (the Assign drawer's shapes), or null for a task. */
export const targetOfStep = (
  st: StepDraft,
): { type: string; ref?: string; source?: string | null } | null => {
  if (st.keptTarget) {
    const { type, ref, source } = st.keptTarget;
    return { type, ...(ref ? { ref } : {}), ...(source ? { source } : {}) };
  }
  if (st.kind === 'task') return null;
  if (!st.targetRef) return null;
  if (st.kind === 'video') return videoTargetOf(st.targetRef);
  const type = targetTypeOf(st.kind as AssignableKind, st.targetRef);
  if (!type) return null;
  return st.kind === 'tool' ? { type } : { type, ref: st.targetRef };
};

/** What is still missing before Save — the first problem's i18n key and where, or null. */
export const draftProblem = (
  d: PathwayDraft,
): { key: string; stepId?: string } | null => {
  if (!d.name.trim()) return { key: 'pathways.builder.required.name' };
  const steps = d.sections.flatMap((s) => s.steps);
  if (!steps.length) return { key: 'pathways.builder.required.steps' };
  if (d.sections.some((s) => !s.section.trim())) return { key: 'pathways.builder.required.section' };
  for (let i = 0; i < steps.length; i++) {
    const st = steps[i];
    if (!st.title.trim()) return { key: 'pathways.builder.required.stepTitle', stepId: st.stepId };
    if (st.kind === 'video' && st.targetRef && !videoTargetOf(st.targetRef))
      return { key: 'pathways.builder.required.video', stepId: st.stepId };
    if (st.kind !== 'task' && !targetOfStep(st))
      return { key: 'pathways.builder.required.target', stepId: st.stepId };
    if (st.kind === 'link' && st.targetRef && !/^https:\/\/\S+$/.test(st.targetRef.trim()))
      return { key: 'pathways.builder.required.link', stepId: st.stepId };
    if (i === 0 && st.rule === 'afterPrevious')
      return { key: 'pathways.builder.firstStepRule', stepId: st.stepId };
    if (st.rule === 'onDay' && (st.day === null || st.day < 0))
      return { key: 'pathways.builder.required.day', stepId: st.stepId };
  }
  return null;
};

/** The wire input of DefinePathway / RevisePathway (the both-ends codec validates it). */
export const inputOfDraft = (d: PathwayDraft) => ({
  name: d.name.trim(),
  description: d.description.trim() || null,
  visibility: d.visibility,
  sections: d.sections.map((s) => ({
    section: s.section.trim(),
    steps: s.steps.map((st) => ({
      stepId: st.stepId,
      kind: st.kind,
      target: targetOfStep(st),
      title: st.title.trim(),
      instructions: st.instructions.trim() || null,
      release: {
        rule: st.rule,
        ...(st.rule === 'onDay' ? { day: st.day ?? 0 } : {}),
        ...(st.rule === 'afterPrevious' ? { delayDays: st.delayDays ?? 0 } : {}),
      },
      dueDays: st.dueDays,
    })),
  })),
});

/** Move an item one place up or down (the step list's reorder). */
export const moved = <T>(xs: readonly T[], from: number, by: -1 | 1): T[] => {
  const to = from + by;
  if (to < 0 || to >= xs.length) return [...xs];
  const out = [...xs];
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x);
  return out;
};

// ── the Enroll drawer's preview: when each step lands ─────────────────────────────────────────────

export type StepLanding =
  | { when: 'atStart'; dueDays: number | null }
  | { when: 'onDay'; day: number; dueDays: number | null }
  | { when: 'afterPrevious'; previousTitle: string; delayDays: number; dueDays: number | null }
  | { when: 'manual'; dueDays: number | null };

/** "Read: What anxiety does — at enrolment · due +3 d" … (the drawer's preview; keys and numbers only). */
export const landingsOf = (
  sections: ReadonlyArray<{ steps: ReadonlyArray<{ title: string; release: { rule: string; day?: number | null; delayDays?: number | null }; dueDays?: number | null }> }>,
): Array<{ title: string; landing: StepLanding }> => {
  const steps = sections.flatMap((s) => s.steps);
  return steps.map((st, i) => {
    const dueDays = st.dueDays ?? null;
    switch (st.release.rule) {
      case 'immediately':
        return { title: st.title, landing: { when: 'atStart', dueDays } };
      case 'onDay':
        return { title: st.title, landing: { when: 'onDay', day: st.release.day ?? 0, dueDays } };
      case 'afterPrevious':
        return {
          title: st.title,
          landing: {
            when: 'afterPrevious',
            previousTitle: steps[i - 1]?.title ?? '',
            delayDays: st.release.delayDays ?? 0,
            dueDays,
          },
        };
      default:
        return { title: st.title, landing: { when: 'manual', dueDays } };
    }
  });
};

/**
 * Is this pathway enrolable by the practice now? A paid CopingCard course, or a copy of one, needs the
 * practice's prescribe courses capability ("Your plan does not include courses" → Billing, Q-e34-22 A).
 */
export const needsPrescribe = (row: Pick<PathwayRow, 'accessRule'>): boolean =>
  row.accessRule === 'purchase' || row.accessRule === 'plan';

/** The enrol drawer's picker: each original followed by its copies ("copy of …"), retired dropped. */
export const pickerOrder = (rows: readonly PathwayRow[]): Array<PathwayRow & { depth: number }> => {
  const live = rows.filter((r) => r.status !== 'retired');
  const ids = new Set(live.map((r) => r.pathwayId));
  const out: Array<PathwayRow & { depth: number }> = [];
  const seen = new Set<string>();
  const walk = (r: PathwayRow, depth: number) => {
    if (seen.has(r.pathwayId)) return;
    seen.add(r.pathwayId);
    out.push({ ...r, depth });
    live
      .filter((c) => c.copiedFrom === r.pathwayId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((c) => walk(c, 1));
  };
  live
    .filter((r) => !r.copiedFrom || !ids.has(r.copiedFrom))
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((r) => walk(r, 0));
  return out;
};

/** The progress's open steps (released, no outcome, not withdrawn) — what a Withdraw cancels. */
export const openStepsOf = (p: Pick<PathwayProgressRow, 'sections'>) =>
  p.sections.flatMap((s) => s.steps).filter((st) => st.state === 'released' && !st.cancelled);

export const doneStepsOf = (p: Pick<PathwayProgressRow, 'sections'>) =>
  p.sections.flatMap((s) => s.steps).filter((st) => st.state === 'done' || st.state === 'skipped');

/** The chip colour of a step's state. */
export const stepStateColor = (state: string): string =>
  state === 'done' ? 'green' : state === 'skipped' ? 'orange' : state === 'released' ? 'blue' : 'default';
